import { z } from 'zod';
import { transitionsPossibles, type StatutCandidature } from './candidature.js';

const DATE_JOUR = /^\d{4}-\d{2}-\d{2}$/;
const jour = z.string().regex(DATE_JOUR, 'Date attendue au format AAAA-MM-JJ');

export const STATUTS_EMPLOYE = ['actif', 'sorti'] as const;
export const statutEmployeSchema = z.enum(STATUTS_EMPLOYE);
export type StatutEmploye = z.infer<typeof statutEmployeSchema>;

export const LIBELLES_STATUT_EMPLOYE: Record<StatutEmploye, string> = {
  actif: 'En poste',
  sorti: 'Sorti',
};

/** Motifs de sortie, contraints en base (migration 009). */
export const MOTIFS_SORTIE = [
  'fin_periode_essai',
  'demission',
  'fin_contrat',
  'licenciement',
  'abandon_poste',
  'autre',
] as const;
export type MotifSortie = (typeof MOTIFS_SORTIE)[number];

export const LIBELLES_MOTIF_SORTIE: Record<MotifSortie, string> = {
  fin_periode_essai: 'Fin de période d’essai',
  demission: 'Démission',
  fin_contrat: 'Fin de contrat',
  licenciement: 'Licenciement',
  abandon_poste: 'Abandon de poste',
  autre: 'Autre',
};

/** POST /candidatures/:id/embaucher */
export const embaucheSchema = z
  .object({
    dateEntree: jour,
    poste: z.string().trim().min(2, 'Poste à préciser').max(120),
    pointDeVenteId: z.string().uuid('Point de vente obligatoire'),
    matricule: z.string().trim().max(30).nullable().optional(),
  })
  .strict();
export type Embauche = z.infer<typeof embaucheSchema>;

/** PUT /employes/:id — l'entrée ne se corrige pas : elle date l'embauche. */
export const modificationEmployeSchema = embaucheSchema.omit({ dateEntree: true });
export type ModificationEmploye = z.infer<typeof modificationEmployeSchema>;

/** POST /employes/:id/sortie */
export const sortieSchema = z
  .object({
    date: jour,
    motif: z.enum(MOTIFS_SORTIE),
    commentaire: z.string().trim().max(500).nullable().optional(),
  })
  .strict()
  .refine((sortie) => sortie.motif !== 'autre' || Boolean(sortie.commentaire), {
    message: 'Préciser le motif « Autre »',
    path: ['commentaire'],
  });
export type Sortie = z.infer<typeof sortieSchema>;

/** L'embauche clôt la chaîne : elle suit « justificatifs reçus » (docs/MODELE_DONNEES.md § Statuts). */
export function peutEmbaucher(statut: StatutCandidature): boolean {
  return transitionsPossibles(statut).includes('embauche');
}

export function erreurDateSortie(dateEntree: string, dateSortie: string): string | null {
  return dateSortie < dateEntree ? 'La date de sortie précède la date d’entrée' : null;
}

/** Jour calendaire de Tunis, `AAAA-MM-JJ`. */
export function jourDeTunis(date: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Africa/Tunis',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(date);
}

/** « 3 mois », « 1 an et 2 mois », « moins d’un mois », « entrée à venir ». */
export function anciennete(dateEntree: string, jusquau: string): string {
  if (jusquau < dateEntree) return 'entrée à venir';
  const [a1, m1, j1] = dateEntree.split('-').map(Number) as [number, number, number];
  const [a2, m2, j2] = jusquau.split('-').map(Number) as [number, number, number];
  const total = (a2 - a1) * 12 + (m2 - m1) - (j2 < j1 ? 1 : 0);
  if (total < 1) return 'moins d’un mois';
  const ans = Math.floor(total / 12);
  const mois = total % 12;
  return [ans ? `${ans} an${ans > 1 ? 's' : ''}` : '', mois ? `${mois} mois` : '']
    .filter(Boolean)
    .join(' et ');
}

export interface EmployeResume {
  id: string;
  personneId: string;
  nom: string | null;
  prenom: string | null;
  matricule: string | null;
  famille: string | null;
  familleCode: string | null;
  pointDeVente: string | null;
  pointDeVenteId: string | null;
  poste: string | null;
  dateEntree: string;
  dateSortie: string | null;
  statut: StatutEmploye;
  /** Candidature d'origine : la fiche candidat devient l'antécédent. */
  candidatureId: string | null;
}

/** Les coordonnées et le commentaire de sortie sont absents pour le rôle `lecteur`. */
export interface EmployeDetail extends EmployeResume {
  telephone: string | null;
  email: string | null;
  ville: string | null;
  adresse: string | null;
  motifSortie: MotifSortie | null;
  commentaireSortie: string | null;
  creeLe: string;
  annonceReference: string | null;
  annonceIntitule: string | null;
  noteEntretien: number | null;
  potentiel: number | null;
}

/** Lien de la fiche candidat vers la fiche employé. */
export interface LienEmploye {
  id: string;
  statut: StatutEmploye;
  dateEntree: string;
  dateSortie: string | null;
  matricule: string | null;
}
